import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { SettingsDialogComponent } from '@app/shared/settings-dialog/settings-dialog.component';

/**
 * Opens the settings dialog the right way:
 *  - the dialog is opened DIRECTLY (no routing), so the page underneath
 *    stays fully rendered and nothing is unmounted;
 *  - the address bar reflects `/settings?tab=…` while it's open, so the URL
 *    looks shareable;
 *  - on close, the ORIGINAL URL is restored, so the router never tries to
 *    resolve `/settings` (which has no route) and never bounces to home.
 *
 * SSR-safe: does nothing on the server.
 */
@Injectable({ providedIn: 'root' })
export class SettingsDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly location = inject(Location);
  private readonly platformId = inject(PLATFORM_ID);

  open(tab: string = 'profile'): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Remember the real URL so we can put it back on close.
    const originalUrl = this.location.path(true) || '/';

    // Reflect a shareable URL WITHOUT routing — the page underneath stays put.
    this.location.replaceState(`/settings?tab=${tab}`);

    const ref = this.dialog.open(SettingsDialogComponent, {
      data: { selectedItem: tab },
      autoFocus: false,
      width: '1000px',
      maxWidth: '96vw',
      panelClass: 'settings-dialog-panel',
    });

    ref.afterClosed().subscribe(() => {
      this.location.replaceState(originalUrl);
    });
  }
}